import type { Chapter45Block, TableCell } from './types';

/**
 * Create a block ID using hyphen-separated format.
 * Format: type-index (e.g., p-10, h1-0, t-5)
 */
function makeBlockId(type: string, index: number): string {
  return `${type}-${index}`;
}

/**
 * Parse an HTML table string and extract rows with cell spans.
 * Returns rows where each cell has content, rowspan, colspan, and isHeader.
 */
function parseHtmlTable(tableHtml: string): {
  rows: TableCell[][];
  cellBlockIds: string[][];
  blockId: string;
} {
  const rows: TableCell[][] = [];

  // Extract all <tr> elements
  const trMatches = tableHtml.matchAll(/<tr[^>]*>([\s\S]*?)<\/tr>/gi);

  for (const trMatch of trMatches) {
    const trContent = trMatch[1] ?? '';
    const cells: TableCell[] = [];

    // Match both <th> and <td> cells
    const cellMatches = trContent.matchAll(
      /<(th|td)([^>]*)>([\s\S]*?)<\/\1>/gi,
    );

    for (const cellMatch of cellMatches) {
      const tagName = cellMatch[1]?.toLowerCase() ?? 'td';
      const attributes = cellMatch[2] ?? '';
      const content = cellMatch[3]?.trim() ?? '';

      // Extract rowspan and colspan attributes
      const rowspanMatch = attributes.match(/rowspan\s*=\s*["']?(\d+)["']?/i);
      const colspanMatch = attributes.match(/colspan\s*=\s*["']?(\d+)["']?/i);

      const rowspan = rowspanMatch ? parseInt(rowspanMatch[1]!, 10) : undefined;
      const colspan = colspanMatch ? parseInt(colspanMatch[1]!, 10) : undefined;

      cells.push({
        content,
        rowspan: rowspan && rowspan > 1 ? rowspan : undefined,
        colspan: colspan && colspan > 1 ? colspan : undefined,
        isHeader: tagName === 'th',
      });
    }

    if (cells.length > 0) {
      rows.push(cells);
    }
  }

  // Generate a temporary blockId (will be replaced by caller)
  const blockId = 't-0';
  const cellBlockIds = rows.map((row, r) =>
    row.map((_cell, c) => `${blockId}-r${r}-c${c}`),
  );

  return { rows, cellBlockIds, blockId };
}

/**
 * Collect an HTML block starting from the given line index until the closing tag.
 * Works for both <table> and <div> blocks.
 * Returns the full HTML and the number of lines consumed.
 */
function collectHtmlBlock(
  lines: string[],
  startIndex: number,
  tagName: 'table' | 'div',
): { blockHtml: string; linesConsumed: number } {
  let blockHtml = '';
  let linesConsumed = 0;
  let depth = 0;

  const openPattern = new RegExp(`<${tagName}`, 'gi');
  const closePattern = new RegExp(`</${tagName}>`, 'gi');

  for (let i = startIndex; i < lines.length; i++) {
    const line = lines[i] ?? '';
    blockHtml += line + '\n';
    linesConsumed++;

    // Count opening and closing tags
    const openTags = (line.match(openPattern) ?? []).length;
    const closeTags = (line.match(closePattern) ?? []).length;
    depth += openTags - closeTags;

    // Stop when we've closed all opened tags
    if (depth <= 0 && blockHtml.includes(`</${tagName}>`)) {
      break;
    }
  }

  return { blockHtml, linesConsumed };
}

/**
 * Collect an HTML table block starting from the given line index.
 * Returns the full table HTML and the number of lines consumed.
 */
function collectHtmlTableBlock(
  lines: string[],
  startIndex: number,
): { tableHtml: string; linesConsumed: number } {
  const { blockHtml, linesConsumed } = collectHtmlBlock(
    lines,
    startIndex,
    'table',
  );
  return { tableHtml: blockHtml, linesConsumed };
}

/**
 * Parse an HTML block (div containing table(s) + legend) and extract components.
 * Returns an array of tables (each with rows) and legend paragraphs.
 * Handles the case where multiple tables exist within a single div.
 */
function parseHtmlBlock(blockHtml: string): {
  tables: Array<{ rows: TableCell[][] }>;
  legendParagraphs: string[];
} {
  // Extract ALL tables from within the div using matchAll
  const tableMatches = blockHtml.matchAll(/<table[\s\S]*?<\/table>/gi);
  const tables: Array<{ rows: TableCell[][] }> = [];

  for (const match of tableMatches) {
    const tableHtml = match[0];
    const { rows } = parseHtmlTable(tableHtml);
    if (rows.length > 0) {
      tables.push({ rows });
    }
  }

  // Extract legend content (everything after all tables and before </div>)
  // Remove all table tags to get remaining content
  const afterTables = blockHtml.replace(/<table[\s\S]*?<\/table>/gi, '');
  const legendContent = afterTables
    .replace(/<\/?div[^>]*>/gi, '') // Remove div tags
    .trim();

  // Split legend into paragraphs (non-empty lines)
  const legendParagraphs = legendContent
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  return { tables, legendParagraphs };
}

/**
 * Parse `chapter_45_content` into deterministic blocks that match what the viewer renders.
 *
 * This parser supports both:
 * - Markdown tables (pipe-delimited) for backward compatibility
 * - HTML tables with rowspan/colspan for new extractions
 *
 * Note: Curly braces are NOT stripped here. They are handled by the viewer component
 * which both strips them for display AND adjusts anchor positions accordingly.
 * This ensures anchors computed against text with curly braces still work correctly.
 */
export function parseChapter45(content: string): Chapter45Block[] {
  const lines = content.split('\n');
  const blocks: Chapter45Block[] = [];

  let listItems: Array<{ indent: boolean; text: string }> = [];
  let markdownTableRows: TableCell[][] = [];
  let inMarkdownTable = false;

  const flushList = () => {
    if (listItems.length === 0) return;

    const blockIndex = blocks.length;
    const blockId = makeBlockId('list', blockIndex);

    blocks.push({
      type: 'list',
      blockId,
      items: listItems.map((item, i) => ({
        blockId: `${blockId}-item-${i}`,
        indent: item.indent,
        text: item.text,
      })),
    });

    listItems = [];
  };

  const flushMarkdownTable = () => {
    if (markdownTableRows.length === 0) return;

    const blockIndex = blocks.length;
    const blockId = makeBlockId('t', blockIndex);
    const cellBlockIds = markdownTableRows.map((row, r) =>
      row.map((_cell, c) => `${blockId}-r${r}-c${c}`),
    );

    blocks.push({
      type: 'table',
      blockId,
      rows: markdownTableRows,
      cellBlockIds,
    });

    markdownTableRows = [];
    inMarkdownTable = false;
  };

  for (let index = 0; index < lines.length; index++) {
    const line = lines[index] ?? '';
    const trimmed = line.trim();

    // Empty line ends list but not necessarily table
    if (!trimmed) {
      flushList();
      continue;
    }

    // Horizontal rule
    if (trimmed === '---') {
      flushList();
      flushMarkdownTable();
      blocks.push({ type: 'hr', blockId: makeBlockId('hr', blocks.length) });
      continue;
    }

    // HTML block detection (div wrapper for table(s) + legend)
    // This must come before table detection since the div contains the table
    if (trimmed.startsWith('<div')) {
      flushList();
      flushMarkdownTable();

      const { blockHtml, linesConsumed } = collectHtmlBlock(
        lines,
        index,
        'div',
      );
      const { tables, legendParagraphs } = parseHtmlBlock(blockHtml);

      // Create a block for each table found in the div
      // Legend paragraphs are attached to the last table only
      for (let tableIndex = 0; tableIndex < tables.length; tableIndex++) {
        const table = tables[tableIndex]!;
        const isLastTable = tableIndex === tables.length - 1;

        const blockIndex = blocks.length;
        const blockId = makeBlockId('t', blockIndex);
        const cellBlockIds = table.rows.map((row, r) =>
          row.map((_cell, c) => `${blockId}-r${r}-c${c}`),
        );

        blocks.push({
          type: 'html-block',
          blockId,
          rows: table.rows,
          cellBlockIds,
          // Attach legend only to the last table in the div
          legendParagraphs: isLastTable
            ? legendParagraphs.map((text, i) => ({
                blockId: `${blockId}-legend-${i}`,
                text,
              }))
            : [],
        });
      }

      // Skip the lines we consumed (minus 1 because the for loop will increment)
      index += linesConsumed - 1;
      continue;
    }

    // HTML table detection (standalone tables without div wrapper - backward compatibility)
    if (trimmed.startsWith('<table')) {
      flushList();
      flushMarkdownTable();

      const { tableHtml, linesConsumed } = collectHtmlTableBlock(lines, index);
      const parsed = parseHtmlTable(tableHtml);

      const blockIndex = blocks.length;
      const blockId = makeBlockId('t', blockIndex);
      const cellBlockIds = parsed.rows.map((row, r) =>
        row.map((_cell, c) => `${blockId}-r${r}-c${c}`),
      );

      blocks.push({
        type: 'table',
        blockId,
        rows: parsed.rows,
        cellBlockIds,
      });

      // Skip the lines we consumed (minus 1 because the for loop will increment)
      index += linesConsumed - 1;
      continue;
    }

    // Markdown table rows (pipe-delimited) - backward compatibility
    if (trimmed.startsWith('|')) {
      flushList();
      inMarkdownTable = true;

      // Skip separator rows like: | --- | --- |
      if (trimmed.includes('---')) {
        continue;
      }

      // Parse cells and convert to TableCell format
      const cellStrings = trimmed
        .split('|')
        .filter((cell) => cell.trim() !== '')
        .map((cell) => cell.trim());

      // Convert string[] to TableCell[] (no spans for Markdown tables)
      const cells: TableCell[] = cellStrings.map((content) => ({
        content,
      }));

      markdownTableRows.push(cells);
      continue;
    }

    // If we were in a markdown table and now we're not, flush it
    if (inMarkdownTable) {
      flushMarkdownTable();
    }

    // Headers
    if (trimmed.startsWith('####')) {
      flushList();
      blocks.push({
        type: 'heading',
        level: 4,
        blockId: makeBlockId('h4', blocks.length),
        text: trimmed.slice(4).trim(),
      });
      continue;
    }

    if (trimmed.startsWith('###')) {
      flushList();
      blocks.push({
        type: 'heading',
        level: 3,
        blockId: makeBlockId('h3', blocks.length),
        text: trimmed.slice(3).trim(),
      });
      continue;
    }

    if (trimmed.startsWith('##')) {
      flushList();
      blocks.push({
        type: 'heading',
        level: 2,
        blockId: makeBlockId('h2', blocks.length),
        text: trimmed.slice(2).trim(),
      });
      continue;
    }

    // H1 - main chapter title (single #)
    if (trimmed.startsWith('#') && !trimmed.startsWith('##')) {
      flushList();
      blocks.push({
        type: 'heading',
        level: 1,
        blockId: makeBlockId('h1', blocks.length),
        text: trimmed.slice(1).trim(),
      });
      continue;
    }

    // List items
    if (trimmed.startsWith('* ') || trimmed.startsWith('- ')) {
      const itemContent = trimmed.slice(2);
      const indent = line.search(/\S/) > 0;
      listItems.push({
        indent,
        text: itemContent,
      });
      continue;
    }

    // Continuation of list item (indented text following a list item)
    // This handles formats like:
    // - *Name*
    //   Description continues here...
    if (listItems.length > 0 && line.startsWith('  ')) {
      // Append to the last list item with a newline
      const lastItem = listItems[listItems.length - 1];
      if (lastItem) {
        lastItem.text = lastItem.text + '\n' + trimmed;
      }
      continue;
    }

    // Non-indented text after a list item ends the list and starts a new paragraph.
    // Only indented lines (starting with spaces) are treated as list item continuations.
    if (listItems.length > 0) {
      flushList();
      blocks.push({
        type: 'paragraph',
        blockId: makeBlockId('p', blocks.length),
        text: trimmed,
      });
      continue;
    }

    // Regular paragraph
    blocks.push({
      type: 'paragraph',
      blockId: makeBlockId('p', blocks.length),
      text: trimmed,
    });
  }

  flushList();
  flushMarkdownTable();

  return blocks;
}

/**
 * Serialize parsed Chapter 4.5 blocks to a string format for LLM consumption.
 * Each block is prefixed with its ID for easy reference in LLM responses.
 *
 * Format:
 * - Non-table blocks: `[id: p-10] content`
 * - Tables: Full HTML with id attributes on table, tr, th, td elements
 */
export function serializeBlocksForLLM(blocks: Chapter45Block[]): string {
  const lines: string[] = [];

  for (const block of blocks) {
    switch (block.type) {
      case 'heading': {
        // Don't include ### markers - the block ID (h1, h2, h3, h4) already indicates heading level
        // Including markers causes mismatch: LLM quotes "### Text" but parser stores just "Text"
        lines.push(`[id: ${block.blockId}] ${block.text}`);
        break;
      }
      case 'paragraph':
        lines.push(`[id: ${block.blockId}] ${block.text}`);
        break;
      case 'list':
        for (const item of block.items) {
          // Don't include "- " marker - the block ID (list-X-item-Y) already indicates it's a list item
          // Including markers causes mismatch: LLM quotes "- Text" but parser stores just "Text"
          lines.push(`[id: ${item.blockId}] ${item.text}`);
        }
        break;
      case 'table':
      case 'html-block': {
        // Generate HTML table with IDs on all elements
        lines.push(`[id: ${block.blockId}]`);
        lines.push(`<table id="${block.blockId}">`);
        for (let r = 0; r < block.rows.length; r++) {
          const row = block.rows[r]!;
          const rowId = `${block.blockId}-r${r}`;
          lines.push(`  <tr id="${rowId}">`);
          for (let c = 0; c < row.length; c++) {
            const cell = row[c]!;
            const cellId = block.cellBlockIds[r]![c]!;
            const tag = cell.isHeader ? 'th' : 'td';
            const rowspanAttr =
              cell.rowspan && cell.rowspan > 1
                ? ` rowspan="${cell.rowspan}"`
                : '';
            const colspanAttr =
              cell.colspan && cell.colspan > 1
                ? ` colspan="${cell.colspan}"`
                : '';
            lines.push(
              `    <${tag} id="${cellId}"${rowspanAttr}${colspanAttr}>${cell.content}</${tag}>`,
            );
          }
          lines.push(`  </tr>`);
        }
        lines.push(`</table>`);
        // Add legend paragraphs if present (html-block only)
        if (block.type === 'html-block') {
          for (const legend of block.legendParagraphs) {
            lines.push(`[id: ${legend.blockId}] ${legend.text}`);
          }
        }
        break;
      }
      case 'hr':
        lines.push('---');
        break;
    }
  }

  return lines.join('\n');
}
